import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useParams } from 'react-router-dom';
import { createSchool } from '../../../api/services/schoolService';
import { getProgramsByDistrict } from '../../../api/services/programService';
import { getDistrict } from '../../../api/services/districtService';
import { ResponseStatus } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { getStates } from '../../../api/services/stateService';
import LayoutError from '../../../components/layoutError';

type Params = {
  districtId: string;
};

const fetchPrograms = async ({ districtId }: any) => {
  return await getProgramsByDistrict(districtId);
};

const fetchDistrict = async ({ districtId }: any) => {
  return await getDistrict(districtId);
};

const CreateSchools = () => {
  const history = useHistory();
  const { districtId } = useParams<Params>();
  const [schoolName, setSchoolName] = useState('');
  const [address, setAddress] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<number | undefined>(0);
  const [programs, setPrograms] = useState<(number | undefined)[]>([]);

  const statesData = useAsync({
    promiseFn: getStates,
  });

  const programsData = useAsync({
    promiseFn: fetchPrograms,
    districtId,
  });

  useAsync({
    promiseFn: fetchDistrict,
    districtId,
    onResolve: (r) => {
      setState(r.data.district.state.stateId);
    },
  });

  const saveSchool = async () => {
    return await createSchool({
      name: schoolName,
      districtId: Number(districtId),
      address: address,
      address2: address2,
      city: city,
      zip: zip,
      isVisible: visible,
      stateId: state === 0 ? undefined : state,
      programIds: programs,
    });
  };

  const save = useAsync({
    deferFn: saveSchool,
    onResolve: () => {
      history.push(`/admin/districts/${districtId}`, {
        message: `${schoolName} added successfully!`,
      });
    },
  });

  // Error handling
  if (programsData.error) return <LayoutError />;

  const handleSubmit = () => {
    save.run();
  };

  const handleProgramChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setPrograms([...programs, parseInt(e.currentTarget.value)]);
    } else {
      setPrograms([
        ...programs.filter((t) => t !== parseInt(e.currentTarget.value)),
      ]);
    }
  };

  return (
    <Layout isLoading={programsData.isPending}>
      <Fragment>
        <ResponseStatus state={save} />
        <div className='grid grid-cols-6 gap-6 mb-6'>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='school'
              label='School Name'
              minLength={1}
              onChange={(e) => setSchoolName(e.target.value)}
              value={schoolName}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='address'
              label='Address'
              onChange={(e) => setAddress(e.target.value)}
              value={address}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='address2'
              label='Address2'
              onChange={(e) => setAddress2(e.target.value)}
              value={address2}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='city'
              label='City'
              onChange={(e) => setCity(e.target.value)}
              value={city}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <label className='primary'>State</label>
            <select
              onChange={(e) => setState(Number(e.target.value))}
              className='block primary mt-1'
              value={state}
            >
              <option value='0'> -- Choose State --</option>
              {statesData.data?.data.states.sort((a, b) => {
                return a.name.localeCompare(b.name);
              }).map((s) => (
                <option value={s.stateId}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <FieldInput
              name='zip'
              label='Zip'
              onChange={(e) => setZip(e.target.value)}
              value={zip}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <div className='flex items-start'>
              <div className='flex items-center h-5'>
                <input
                  id='is-visible'
                  name='is-visible'
                  type='checkbox'
                  className='primary-checkbox'
                  checked={visible}
                  onChange={(e) => setVisible(e.currentTarget.checked)}
                />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='is-visible' className='primary'>
                  Is Visible
                </label>
                <p className='text-gray-500'>
                  When checked, this school will be available in the timesheet
                  entry flow.
                </p>
              </div>
            </div>
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <label className='primary'>Programs</label>

            {programsData.data?.data.programs !== undefined &&
              programsData.data?.data.programs.length > 0 && (
                <Fragment>
                  <p className='text-sm text-gray-500 mb-3'>
                    Select all applicable programs.
                  </p>
                  <div className='mt-1 rounded-lg bg-white shadow px-4 py-3'>
                    {programsData.data?.data.programs
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p, i) => {
                        const pid = `program_${i}`;

                        return (
                          <div key={pid} className='flex items-start py-1'>
                            <div className='flex items-center h-5'>
                              <input
                                id={pid}
                                value={p.programId}
                                checked={programs.includes(p.programId)}
                                onChange={handleProgramChange}
                                name='program'
                                type='checkbox'
                                className='primary-checkbox'
                              />
                            </div>
                            <div className='ml-3 text-sm'>
                              <label htmlFor={pid} className='primary'>
                                {p.name}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </Fragment>
              )}
            {(programsData.data?.data.programs === undefined ||
              programsData.data?.data.programs.length === 0) && (
                <p className='text-sm text-gray-500 mb-3'>
                  No applicable programs.
                </p>
              )}
          </div>
        </div>
        <div className='text-right'>
          <SubmitButton
            text='Create School'
            spinning={save.isPending}
            disabled={schoolName.length === 0}
            onClick={handleSubmit}
          />
        </div>
      </Fragment>
    </Layout>
  );
};

export default CreateSchools;
